import { useEffect, useState } from 'react';
import Hammer from 'react-hammerjs';
import ViewSongPart from '../ViewSongPart';
import SongCount from '../SongCount';
import ViewNextSongBtn from '../ViewNextSongBtn';
import SongHeaderSection from '../SongHeaderSection';
import SongTransition from '../SongTransition';
import { useGlobalContext } from '../../../../context/GlobalContext';
import { ChartRunProvider } from '../../../../context/ChartRunContext';
import { useViewHeaderStore } from '../ViewHeader/ViewHeader';
import { startScroll, stopScroll } from '../../../../utils/autoScrollHelpers';
import useSmartScrollSchedule from '../../../../hooks/useSmartScrollSchedule';
import usePlayingPartHighlight from '../../../../hooks/usePlayingPartHighlight';
import useFontSize from '../../../../hooks/useFontSize';
import { isHiddenInOverview } from '../../../../utils/parts';
import { getAttachedNotes, isAttachedNote } from '../../../../utils/notes';
import { computePartNumbers } from '../../../../utils/partNumbers';
import { getPartKeys, getSongEndKey, isSameKey } from '../../../../utils/partKey';
import { getPartTempos } from '../../../../utils/partTempo';
import usePartTempoMetronome from '../plugins/MetronomePlugin/usePartTempoMetronome';

import './ViewBody.scss';

const ViewBody = ({
  song,
  songs,
  service,
  onPrevious,
  onNext,
  continuousView,
  resetAutoTranspose,
  onManualTranspose,
  hideChords,
  overview,
}) => {
  const currentFontSize = useFontSize();
  const playPartTempo = usePartTempoMetronome();
  const { scrollTimerRef, isScrolling, setIsScrolling, scrollingSpeed, setScrollingSpeed } = useGlobalContext();

  // Track transposed keys for each song by index
  const [transposedKeys, setTransposedKeys] = useState({});

  // Subscribe to the first song's key from ViewHeader store (for continuous view)
  const firstSongKey = useViewHeaderStore((state) => state.songKey);
  const storeSongId = useViewHeaderStore((state) => state.songId);

  // The parts of a song as they actually appear. An attached note is never shown by itself — it
  // rides along inside its host parts, overview included: it reads as that part's own text, and
  // overview doesn't hide lyrics. Both branches below render from this, and getPartKeys has to be
  // given the same list: it carries each declared key forward from one part to the next, so a part
  // overview has taken off the screen must not be in the list handing keys on.
  const renderedParts = (item) =>
    item.parts
      .filter((part) => !overview || !isHiddenInOverview(part))
      .filter((part) => !isAttachedNote(part, item.parts));

  // The part as it was written, where the page is showing it with the chords taken out: View keeps
  // the untouched parts on the song for as long as Hide Chords is on. The chart player reads them
  // so a chart can still be opened and followed through the words — see ViewSongPart.
  const writtenPart = (item, partId) => item.originalParts?.find((original) => original.id === partId);

  // Smart Auto Scroll measures the parts rendered below, so its schedule is built from here.
  // hideChords and overview both change how tall those parts are, hence the rebuild triggers.
  const scheduledSongs = continuousView && songs?.length ? songs : [song];
  useSmartScrollSchedule(scheduledSongs, { hideChords, overview });
  usePlayingPartHighlight();

  // Initialize transposed keys from songs array
  useEffect(() => {
    if (continuousView && songs.length > 0) {
      const initialKeys = songs.reduce((acc, song, index) => {
        acc[index] = song.key;
        return acc;
      }, {});
      setTransposedKeys(initialKeys);
    }
  }, [songs, continuousView]);

  // Sync firstSongKey only in single song view (not continuous view)
  useEffect(() => {
    if (!continuousView && firstSongKey && songs.length === 1) {
      setTransposedKeys((prev) => ({ ...prev, 0: firstSongKey }));
    }
  }, [firstSongKey, continuousView, songs.length]);

  // Update a song's transposed key
  const handleKeyChange = (songIndex, newKey) => {
    setTransposedKeys((prev) => ({ ...prev, [songIndex]: newKey }));
  };

  const handleStopScrolling = () => {
    if (isScrolling) {
      stopScroll(scrollTimerRef, setIsScrolling);
    } else {
      startScroll(scrollTimerRef, setIsScrolling, setScrollingSpeed, scrollingSpeed, () => {
        stopScroll(scrollTimerRef, setIsScrolling);
      });
    }
  };

  const handleSwipe = (e) => {
    if (service && !continuousView && !isScrolling) {
      if (e.deltaX < 0) onNext();
      else onPrevious();
    }
  };

  const getCurrentSongPosition = () => {
    const index = service?.songsIds.indexOf(song._id);
    return index + 1;
  };

  const getNextSong = () => {
    const currentSongIndex = songs.findIndex((s) => s._id === song._id);
    return songs[currentSongIndex + 1];
  };

  const getNextSongTitle = (nextSong) => {
    return nextSong?.title;
  };

  // Render continuous view when enabled
  if (continuousView && service && songs.length > 0) {
    return (
      <div onDoubleClick={handleStopScrolling} style={{ fontSize: currentFontSize }} id="view-body">
        {songs.map((songItem, index) => {
          const currentKey = transposedKeys[index] || songItem.key;
          // A transpose rewrites song.key in place, so originalKey is the one a stored MIDI clip
          // is anchored against; the offset is counted rather than re-derived from key names
          const songOriginalKey = songItem.originalKey ?? songItem.key;
          const transposeSemitones = songItem.autoTransposeSemitones ?? 0;
          const previousKey = index > 0 ? transposedKeys[index - 1] || songs[index - 1].key : null;
          // Where the song above actually finished. A song in Dm whose last chorus lifted to Em
          // hands the band over from Em, so the marker has to measure the move from there.
          const previousEndKey =
            index > 0 ? getSongEndKey(renderedParts(songs[index - 1]), previousKey) : null;
          const partNumbers = computePartNumbers(songItem.parts);
          const partKeys = getPartKeys(renderedParts(songItem), currentKey);
          const partTempos = getPartTempos(renderedParts(songItem), songItem.bpm);

          return (
            <div key={songItem._id} className="continuous-view__song-section" id={`song-section-${index}`}>
              {index > 0 && (
                <>
                  <SongTransition
                    fromKey={previousKey}
                    viaKey={isSameKey(previousEndKey, previousKey) ? undefined : previousEndKey}
                    toKey={currentKey}
                  />
                  <SongHeaderSection
                    song={songItem}
                    songKey={currentKey}
                    songNumber={index + 1}
                    songIndex={index}
                    fromUrl={`/view/service/${service._id}#${index + 1}`}
                    resetAutoTranspose={resetAutoTranspose}
                    onManualTranspose={onManualTranspose}
                    onKeyChange={(newKey) => handleKeyChange(index, newKey)}
                  />
                </>
              )}
              {/* Per song, so a chart played through runs to the end of this one and stops there */}
              <ChartRunProvider partIds={renderedParts(songItem).map((part) => part.id)}>
                {renderedParts(songItem).map((part) => (
                  <ViewSongPart
                    key={part.id}
                    part={part}
                    displayNumber={partNumbers[part.id]}
                    songTeamId={songItem.team._id}
                    songId={songItem._id}
                    songKey={currentKey}
                    songOriginalKey={songOriginalKey}
                    partKey={partKeys[part.id]?.key}
                    previousPartKey={partKeys[part.id]?.from}
                    songTimeSignature={songItem.timeSignature}
                    songCategory={songItem.category?.name}
                    songChartInstrumentId={songItem.chartInstrumentId}
                    songChartRhythm={songItem.chartRhythm}
                    songChartEffects={songItem.chartEffects}
                    songBpm={songItem.bpm}
                    partBpm={partTempos[part.id]?.bpm}
                    tempoEnter={partTempos[part.id]?.enter}
                    tempoExit={partTempos[part.id]?.exit}
                    onTempoTap={playPartTempo}
                    transposeSemitones={transposeSemitones}
                    attachedNotes={getAttachedNotes(songItem.parts, part.id)}
                    writtenPart={writtenPart(songItem, part.id)}
                  />
                ))}
              </ChartRunProvider>
            </div>
          );
        })}
        <ViewNextSongBtn smooth={false} />
      </div>
    );
  }

  // Normal view (single song)
  const nextSong = getNextSong();
  const partNumbers = computePartNumbers(song.parts);
  // Same derivation the header uses, so a part shows the key currently on screen rather than
  // the stored one after a transpose
  const currentSongKey = storeSongId === song._id ? firstSongKey : song.key;
  // Same reasoning as the continuous branch: anchor the MIDI to the song's own key and shift it
  // by the offset the song has accumulated, rather than diffing two key names
  const songOriginalKey = song.originalKey ?? song.key;
  const transposeSemitones = song.autoTransposeSemitones ?? 0;
  const partKeys = getPartKeys(renderedParts(song), currentSongKey);
  const partTempos = getPartTempos(renderedParts(song), song.bpm);
  // Same idea for the marker at the foot of the song, which announces the move into the next one
  const songEndKey = getSongEndKey(renderedParts(song), currentSongKey);

  return (
    <Hammer onSwipe={handleSwipe}>
      <div onDoubleClick={handleStopScrolling} style={{ fontSize: currentFontSize }} id="view-body">
        {service && <SongCount service={service} song={song} songPosition={getCurrentSongPosition()} songs={songs} />}

        <ChartRunProvider partIds={renderedParts(song).map((part) => part.id)}>
          {renderedParts(song).map((part) => (
            <ViewSongPart
              key={part.id}
              part={part}
              displayNumber={partNumbers[part.id]}
              songTeamId={song.team._id}
              songId={song._id}
              songKey={currentSongKey}
              songOriginalKey={songOriginalKey}
              partKey={partKeys[part.id]?.key}
              previousPartKey={partKeys[part.id]?.from}
              songTimeSignature={song.timeSignature}
              songCategory={song.category?.name}
              songChartInstrumentId={song.chartInstrumentId}
              songChartRhythm={song.chartRhythm}
              songChartEffects={song.chartEffects}
              songBpm={song.bpm}
              partBpm={partTempos[part.id]?.bpm}
              tempoEnter={partTempos[part.id]?.enter}
              tempoExit={partTempos[part.id]?.exit}
              onTempoTap={playPartTempo}
              transposeSemitones={transposeSemitones}
              attachedNotes={getAttachedNotes(song.parts, part.id)}
              writtenPart={writtenPart(song, part.id)}
            />
          ))}
        </ChartRunProvider>

        {service ? (
          <>
            <ViewNextSongBtn
              onNext={onNext}
              nextSongTitle={getNextSongTitle(nextSong)}
              nextSongPosition={getCurrentSongPosition() + 1}
            />
            {nextSong && !hideChords && (
              <SongTransition
                fromKey={currentSongKey}
                viaKey={isSameKey(songEndKey, currentSongKey) ? undefined : songEndKey}
                toKey={nextSong.key}
              />
            )}
          </>
        ) : (
          <ViewNextSongBtn />
        )}
      </div>
    </Hammer>
  );
};

export default ViewBody;
